import { FormGroup, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isValidPhoneNumber } from 'libphonenumber-js';

export class CustomValidators {
    constructor() {}

    static mustNotMatch(controlName: string, matchingControlName: string): ValidatorFn {
        return (abstractControl: AbstractControl): ValidationErrors | null => {
            const formGroup = abstractControl as FormGroup;
            const control = formGroup.controls[controlName];
            const matchingControl = formGroup.controls[matchingControlName];

            // Check if the controls are present to avoid null/undefined values
            if (!control || !matchingControl) {
                return null;
            }

            if (control.value === matchingControl.value) {
                return { mustNotMatch: true };
            }
            return null;
        };
    }

    static mustMatch(controlName: string, matchingControlName: string): ValidatorFn {
        return (abstractControl: AbstractControl): ValidationErrors | null => {
            const formGroup = abstractControl as FormGroup;
            const control = formGroup.controls[controlName];
            const matchingControl = formGroup.controls[matchingControlName];

            // Check if the controls are present to avoid null/undefined values
            if (!control || !matchingControl) {
                return null;
            }

            if (control.value !== matchingControl.value) {
                // Only set errors if matchingControl does not already have an error
                if (!matchingControl.errors?.mustMatch) {
                    matchingControl.setErrors({ mustMatch: true });
                }
                return { mustMatch: true };
            } else {
                // Clear the error if it exists and if there are no other errors
                if (matchingControl.errors && matchingControl.errors.mustMatch) {
                    delete matchingControl.errors['mustMatch'];
                    if (Object.keys(matchingControl.errors).length === 0) {
                        matchingControl.setErrors(null);
                    }
                }
            }
            return null;
        };
    }

    static passwordValidator(control: AbstractControl): ValidationErrors | null {
        const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
        if (regex.exec(control.value) !== null) {
            return null;
        } else {
            return {
                invalidPassword: true,
                errorMsg:
                    'Invalid password. The password must be at least 8 characters long, include at least one uppercase letter (A-Z), one number (0-9), and one special character (such as @, #, $, etc.).',
            };
        }
    }

    static vatValidator(control: AbstractControl): ValidationErrors | null {
        const regex =
            /^(ATU[0-9]{8}|BE[0-1][0-9]{9}|BG[0-9]{9,10}|CY[0-9]{8}[A-Z]{1}|CZ[0-9]{8,10}|DE[0-9]{9}|DK[0-9]{8}|EE[0-9]{9}|(EL|GR)[0-9]{9}|ES([A-Z][0-9]{7}[0-9A-Z]|[0-9][0-9]{7}[A-Z])|FI[0-9]{8}|FR[0-9A-Z]{2}[0-9]{9}|GB([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})|HU[0-9]{8}|IE([0-9][A-Z0-9+*][0-9]{5}[A-Z]|[0-9]{7}[A-Z]{2})|IT[0-9]{11}|LT([0-9]{9}|[0-9]{12})|LU[0-9]{8}|LV[0-9]{11}|MT[0-9]{8}|NL([0-9]{9}B[0-9]{2}|[A-Z0-9+*]{12})|PL[0-9]{10}|PT[0-9]{9}|RO[0-9]{2,10}|SE[0-9]{12}|SI[0-9]{8}|SK[0-9]{10})$/;
        if (regex.exec(control.value) !== null) {
            return null;
        } else {
            return {
                invalidVat: true,
                errorMsg: 'The VAT number is invalid',
            };
        }
    }

    static optionalVatValidator(control: AbstractControl): ValidationErrors | null {
        const regex =
            /^(ATU[0-9]{8}|BE[0-1][0-9]{9}|BG[0-9]{9,10}|CY[0-9]{8}[A-Z]{1}|CZ[0-9]{8,10}|DE[0-9]{9}|DK[0-9]{8}|EE[0-9]{9}|(EL|GR)[0-9]{9}|ES([A-Z][0-9]{7}[0-9A-Z]|[0-9][0-9]{7}[A-Z])|FI[0-9]{8}|FR[0-9A-Z]{2}[0-9]{9}|GB([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})|HU[0-9]{8}|IE([0-9][A-Z0-9+*][0-9]{5}[A-Z]|[0-9]{7}[A-Z]{2})|IT[0-9]{11}|LT([0-9]{9}|[0-9]{12})|LU[0-9]{8}|LV[0-9]{11}|MT[0-9]{8}|NL([0-9]{9}B[0-9]{2}|[A-Z0-9+*]{12})|PL[0-9]{10}|PT[0-9]{9}|RO[0-9]{2,10}|SE[0-9]{12}|SI[0-9]{8}|SK[0-9]{10})$/;

        if (
            control === null ||
            control === undefined ||
            regex.exec(control.value) !== null ||
            control.value === null ||
            control.value === '' ||
            control.value === undefined
        ) {
            return null;
        } else {
            return {
                invalidVat: true,
                errorMsg: 'The VAT number is invalid',
            };
        }
    }

    static emailValidator(control: AbstractControl): ValidationErrors | null {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (regex.exec(control.value) !== null) {
            return null;
        } else {
            return {
                invalidEmail: true,
                errorMsg: 'Invalid Email Format',
            };
        }
    }

    static phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
        if (control.value === undefined || control.value === null || control.value === '')
            return {
                required: true,
            };
        if (control.value === '+3200000000000')
            return {
                invalidPn: true,
                errorMsg: 'Invalid Phone Number Format',
            };

        if (isValidPhoneNumber(control.value)) {
            return null;
        } else {
            return {
                invalidPn: true,
                errorMsg: 'Invalid Phone Number Format',
            };
        }
    }
    static regexValidator(regex: RegExp, errorMsg: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            if (value?.length > 0 && !regex.test(value)) {
                return {
                    invalidVat: true,
                    errorMsg: errorMsg,
                };
            }

            return null;
        };
    }
}
